import React, { useMemo, useState } from "react";
import { connect } from "react-redux";
import { Button, Checkbox, Popover, Table, Tooltip } from "antd";
import { DeleteOutlined, EditOutlined, EyeOutlined } from "@ant-design/icons";
import type { TablePaginationConfig } from "antd/es/table";
import type { FilterValue, SorterResult } from "antd/es/table/interface";
import StatusPill from "../components/StatusPill";
import InfoAffordance from "../components/InfoAffordance";
import ConfirmDialog from "../components/ConfirmDialog";
import { PihTableShell, renderPihColumnTitle, type SortOrder } from "../../common/pihStyleTable";
import "../../common/pih-style-table.css";
import type { RootState } from "../../../redux/appState";
import {
  openConfigModal,
  openConfigurationDetail,
  setConfigStatusFilter,
  deleteConfiguration,
} from "../../../redux/coilConfigurator/actions";
import { PAGE_SIZE, selectFilteredConfigurations } from "../../../redux/coilConfigurator/selectors";
import { formatConfigurationDate, formatConfigurationStatusLabel } from "../../../web-app";
import type { SavedCoilConfiguration } from "../../../web-app";

const STATUS_OPTIONS = ["recommended", "marginal", "not supported"];

interface ConfigurationsListProps {
  allRows: SavedCoilConfiguration[];
  statusFilter: string[];
  onStatusFilter: (values: string[]) => void;
  onOpenDetail: (id: string) => void;
  onCreateConfiguration: () => void;
  onEditConfiguration: (id: string) => void;
  onDeleteConfiguration: (id: string) => void;
}

function getCellSearchValue(record: SavedCoilConfiguration, key: string): string {
  switch (key) {
    case "reference":
      return record.reference;
    case "project":
      return record.project;
    case "pipeOd":
      return record.fieldJointGeometry.pipeOutsideDiameterMm.toFixed(1);
    case "powerSource":
      return record.recommendation.selectedPowerSource.name;
    case "turns":
      return String(record.recommendation.selectedCoilConfiguration?.numberOfTurns ?? "");
    case "status":
      return record.recommendation.recommendationStatus;
    case "utilization":
      return record.recommendation.powerUtilization != null
        ? String(Math.round(record.recommendation.powerUtilization * 100))
        : "";
    case "createdAt":
      return formatConfigurationDate(record.createdAt);
    default:
      return "";
  }
}

const ConfigurationsList = ({
  allRows,
  statusFilter,
  onStatusFilter,
  onOpenDetail,
  onCreateConfiguration,
  onEditConfiguration,
  onDeleteConfiguration,
}: ConfigurationsListProps) => {
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [sortField, setSortField] = useState<string>("reference");
  const [sortOrder, setSortOrder] = useState<SortOrder>("ascend");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [statusFilterOpen, setStatusFilterOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<SavedCoilConfiguration | null>(null);

  const handleColumnFilterChange = (key: string, value: string) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const handleColumnSort = (key: string) => {
    if (sortField !== key) {
      setSortField(key);
      setSortOrder("ascend");
    } else if (sortOrder === "ascend") {
      setSortOrder("descend");
    } else if (sortOrder === "descend") {
      setSortField("reference");
      setSortOrder("ascend");
    } else {
      setSortOrder("ascend");
    }
    setPage(1);
  };

  const titleOptions = (dataIndex: string, extra?: Partial<Parameters<typeof renderPihColumnTitle>[2]>) => ({
    columnFilters,
    onColumnFilterChange: handleColumnFilterChange,
    sortField,
    sortOrder,
    onColumnSort: handleColumnSort,
    ...extra,
  });

  const filteredRows = useMemo(() => {
    return allRows.filter((row) => {
      return Object.entries(columnFilters).every(([key, value]) => {
        const query = value.trim().toLowerCase();
        if (!query) return true;
        return getCellSearchValue(row, key).toLowerCase().includes(query);
      });
    });
  }, [allRows, columnFilters]);

  const sortedRows = useMemo(() => {
    if (!sortField || !sortOrder) return filteredRows;
    const isDesc = sortOrder === "descend";
    return [...filteredRows].sort((a, b) => {
      if (sortField === "createdAt") {
        const aTime = a.createdAt instanceof Date ? a.createdAt.getTime() : new Date(a.createdAt).getTime();
        const bTime = b.createdAt instanceof Date ? b.createdAt.getTime() : new Date(b.createdAt).getTime();
        return isDesc ? bTime - aTime : aTime - bTime;
      }
      const aVal = getCellSearchValue(a, sortField);
      const bVal = getCellSearchValue(b, sortField);
      const cmp = aVal.localeCompare(bVal, undefined, { numeric: true });
      return isDesc ? -cmp : cmp;
    });
  }, [filteredRows, sortField, sortOrder]);

  const paginatedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sortedRows.slice(start, start + pageSize);
  }, [sortedRows, page, pageSize]);

  const statusFilterContent = (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {STATUS_OPTIONS.map((status) => (
        <Checkbox
          key={status}
          checked={statusFilter.includes(status)}
          onChange={(event) => {
            const next = event.target.checked
              ? [...statusFilter, status]
              : statusFilter.filter((value) => value !== status);
            onStatusFilter(next);
            setPage(1);
          }}
        >
          {formatConfigurationStatusLabel(status)}
        </Checkbox>
      ))}
    </div>
  );

  const handleDelete = (record: SavedCoilConfiguration) => {
    setDeleteTarget(record);
  };

  const columns = [
    {
      title: renderPihColumnTitle("Reference", "reference", titleOptions("reference")),
      dataIndex: "reference",
      key: "reference",
      width: 220,
      sorter: true,
      sortOrder: sortField === "reference" ? sortOrder ?? undefined : undefined,
      render: (value: string, record: SavedCoilConfiguration) => (
        <Button type="link" className="cc-table-link" onClick={() => onOpenDetail(record.id)} style={{ padding: 0 }}>
          {value}
        </Button>
      ),
    },
    {
      title: renderPihColumnTitle("Project", "project", titleOptions("project")),
      dataIndex: "project",
      key: "project",
      width: 180,
      sorter: true,
      sortOrder: sortField === "project" ? sortOrder ?? undefined : undefined,
      render: (_: unknown, record: SavedCoilConfiguration) => record.project,
    },
    {
      title: renderPihColumnTitle("Pipe Ø (mm)", "pipeOd", titleOptions("pipeOd")),
      dataIndex: "pipeOd",
      key: "pipeOd",
      width: 130,
      sorter: true,
      sortOrder: sortField === "pipeOd" ? sortOrder ?? undefined : undefined,
      render: (_: unknown, record: SavedCoilConfiguration) => (
        <span className="mono-value">{record.fieldJointGeometry.pipeOutsideDiameterMm.toFixed(1)}</span>
      ),
    },
    {
      title: renderPihColumnTitle("Power Source", "powerSource", {
        ...titleOptions("powerSource"),
        glossaryAffordance: <InfoAffordance glossaryKey="powerSource" />,
      }),
      dataIndex: "powerSource",
      key: "powerSource",
      width: 260,
      sorter: true,
      sortOrder: sortField === "powerSource" ? sortOrder ?? undefined : undefined,
      render: (_: unknown, record: SavedCoilConfiguration) =>
        record.recommendation.selectedPowerSource.name,
    },
    {
      title: renderPihColumnTitle("Turns", "turns", titleOptions("turns")),
      dataIndex: "turns",
      key: "turns",
      width: 100,
      sorter: true,
      sortOrder: sortField === "turns" ? sortOrder ?? undefined : undefined,
      render: (_: unknown, record: SavedCoilConfiguration) => {
        const turns = record.recommendation.selectedCoilConfiguration?.numberOfTurns;
        return turns != null ? <span className="mono-value">{turns}</span> : "—";
      },
    },
    {
      title: (
        <Popover
          content={statusFilterContent}
          trigger="click"
          open={statusFilterOpen}
          onOpenChange={setStatusFilterOpen}
          placement="bottom"
        >
          <div className="cc-status-column-header">
            {renderPihColumnTitle("Status", "status", {
              ...titleOptions("status"),
              hideFilterInput: true,
              showFilterIcon: true,
              filterActive: statusFilter.length < STATUS_OPTIONS.length,
              onFilterIconClick: () => setStatusFilterOpen(true),
            })}
          </div>
        </Popover>
      ),
      dataIndex: "status",
      key: "status",
      width: 150,
      align: "center" as const,
      className: "cc-status-column-cell",
      onHeaderCell: () => ({ className: "cc-status-column-header" }),
      sorter: true,
      sortOrder: sortField === "status" ? sortOrder ?? undefined : undefined,
      render: (_: unknown, record: SavedCoilConfiguration) => (
        <StatusPill status={record.recommendation.recommendationStatus} />
      ),
    },
    {
      title: renderPihColumnTitle("Utilization", "utilization", titleOptions("utilization")),
      dataIndex: "utilization",
      key: "utilization",
      width: 120,
      sorter: true,
      sortOrder: sortField === "utilization" ? sortOrder ?? undefined : undefined,
      render: (_: unknown, record: SavedCoilConfiguration) =>
        record.recommendation.powerUtilization != null ? (
          <span className="mono-value">{Math.round(record.recommendation.powerUtilization * 100)}%</span>
        ) : (
          "—"
        ),
    },
    {
      title: renderPihColumnTitle("Created", "createdAt", titleOptions("createdAt")),
      dataIndex: "createdAt",
      key: "createdAt",
      width: 140,
      sorter: true,
      sortOrder: sortField === "createdAt" ? sortOrder ?? undefined : undefined,
      render: (_: unknown, record: SavedCoilConfiguration) => formatConfigurationDate(record.createdAt),
    },
    {
      title: (
        <div className="column-header-with-filter">
          <div className="column-header-text-wrapper">
            <span>Actions</span>
          </div>
        </div>
      ),
      key: "actions",
      fixed: "right" as const,
      width: 120,
      render: (_: unknown, record: SavedCoilConfiguration) => (
        <div className="heat-profile-actions-cell">
          <Tooltip title="View">
            <button
              type="button"
              className="heat-profile-action-btn"
              onClick={() => onOpenDetail(record.id)}
              aria-label="View configuration"
            >
              <EyeOutlined />
            </button>
          </Tooltip>
          <Tooltip title="Edit">
            <button
              type="button"
              className="heat-profile-action-btn"
              onClick={() => onEditConfiguration(record.id)}
              aria-label="Edit configuration"
            >
              <EditOutlined />
            </button>
          </Tooltip>
          <Tooltip title="Delete">
            <button
              type="button"
              className="heat-profile-action-btn heat-profile-action-btn--danger"
              onClick={() => handleDelete(record)}
              aria-label="Delete configuration"
            >
              <DeleteOutlined />
            </button>
          </Tooltip>
        </div>
      ),
    },
  ];

  const handleTableChange = (
    pagination: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter: SorterResult<SavedCoilConfiguration> | SorterResult<SavedCoilConfiguration>[],
  ) => {
    const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    const field = singleSorter?.field ?? singleSorter?.columnKey;
    if (field != null && singleSorter?.order) {
      setSortField(String(Array.isArray(field) ? field[0] : field));
      setSortOrder(singleSorter.order as SortOrder);
      setPage(1);
    }
    if (pagination.current) setPage(pagination.current);
    if (pagination.pageSize) setPageSize(pagination.pageSize);
  };

  return (
    <PihTableShell
      title="List of Coil Configurations"
      description=""
      itemCount={sortedRows.length}
      // actions={
      //   <Button type="primary" icon={<PlusOutlined />} onClick={onCreateConfiguration}>
      //     + Create Coil Configuration
      //   </Button>
      // }
    >
      <Table<SavedCoilConfiguration>
        className="pipeline-table heat-profile-table"
        columns={columns}
        dataSource={paginatedRows}
        rowKey="id"
        scroll={{ x: 1720 }}
        pagination={{
          current: page,
          pageSize,
          total: sortedRows.length,
          showSizeChanger: true,
          pageSizeOptions: ["6", "10", "20", "50"],
          showTotal: (total) => `${total} items`,
        }}
        onChange={handleTableChange}
        locale={{ emptyText: "No configurations match the current filters." }}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Remove Configuration"
        message={
          deleteTarget
            ? `Are you sure you want to remove "${deleteTarget.reference}"? This cannot be undone.`
            : ""
        }
        confirmLabel="Remove"
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (deleteTarget) onDeleteConfiguration(deleteTarget.id);
          setDeleteTarget(null);
        }}
      />
    </PihTableShell>
  );
};

const mapStateToProps = (state: RootState) => ({
  allRows: selectFilteredConfigurations(state),
  statusFilter: state.coilConfigurator.configurations.statusFilter,
});

const mapDispatchToProps = {
  onStatusFilter: setConfigStatusFilter,
  onOpenDetail: openConfigurationDetail,
  onCreateConfiguration: () => openConfigModal("create"),
  onEditConfiguration: (id: string) => openConfigModal("edit", id),
  onDeleteConfiguration: deleteConfiguration,
};

export default connect(mapStateToProps, mapDispatchToProps)(ConfigurationsList);
